import React, { useState } from 'react'
import classNames from 'classnames'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { DatePicker } from 'uiComponents/popups/datePickerInput'
import {
    MembershipDetailed,
    MembershipPaymentRetry,
    MembershipPaymentStatus,
    MembershipRetryPayment,
} from 'memberships/schema'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import ActionButton from 'uiComponents/buttons'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import Badge from 'uiComponents/badge'
import { Radio } from 'uiComponents/input/radio'
import Currency from 'uiComponents/money/currency'
import { DateFormats, formatISOString, parseDate } from 'utils'
import { Trans, useTranslation } from 'translations'
import './retryPaymentModal.scss'

export interface PaymentRetryChoice {
    relatedMembershipUuids: string[]
    throughDate: string
    paymentDate: string | null
}

interface RetryPaymentModalProps {
    retry: MembershipPaymentRetry
    membership: MembershipDetailed
    accountSlug: string
    onClose: () => void
    onRetry: (choice: PaymentRetryChoice) => void
}

const ISO_DATE = 'yyyy-MM-dd'
const KEY = 'memberships.membershipDetailsPanel.retryPaymentModal'
const sum = (payments: MembershipRetryPayment[]) => payments.reduce((total, p) => total + Number(p.amount), 0)
const monthOf = (p: MembershipRetryPayment) => p.dueDate.slice(0, 7)

const RetryPaymentModal: React.FC<RetryPaymentModalProps> = ({ retry, membership, accountSlug, onClose, onRetry }) => {
    const { t } = useTranslation()
    const today = formatISOString(new Date(), ISO_DATE)
    const monthsFor = (uuids: Set<string>) =>
        [...new Set(retry.payments.filter((p) => uuids.has(p.membershipUuid)).map(monthOf))].sort()
    const [members, setMembers] = useState(
        () => new Set(retry.memberships.filter((m) => m.uuid === membership.uuid).map((m) => m.uuid)),
    )
    const months = monthsFor(members)
    const [monthCount, setMonthCount] = useState(months.length)
    const [chargeNow, setChargeNow] = useState(true)
    const [date, setDate] = useState(today)

    const isPicked = (p: MembershipRetryPayment) => members.has(p.membershipUuid)
    const chosen = retry.payments.filter((p) => isPicked(p) && monthOf(p) <= months[monthCount - 1])
    const throughDate =
        chosen
            .map((p) => p.dueDate)
            .sort()
            .pop() ?? ''
    const total = sum(chosen)
    const currency = retry.payments[0]?.currency
    const allMembers = members.size === retry.memberships.length
    const upcoming = retry.upcoming.filter(isPicked)
    const nextDate = upcoming[0]?.dueDate

    // A new set of memberships brings its own months, all ticked.
    const pickMembers = (next: Set<string>) => {
        setMembers(next)
        setMonthCount(monthsFor(next).length)
    }
    const toggleMember = (uuid: string) => {
        const next = new Set(members)
        next.has(uuid) ? next.delete(uuid) : next.add(uuid)
        pickMembers(next)
    }
    // Oldest first: ticking a month ticks every earlier one, unticking clears every later one.
    const toggleMonth = (index: number) => setMonthCount(index < monthCount ? index : index + 1)
    const pickDate = (value: Date | null) => {
        const picked = value && formatISOString(value, ISO_DATE)
        if (picked && picked >= today && picked <= retry.latestDate) setDate(picked)
    }

    const amount = <Currency amount={total} currency={currency} />
    const blocked = !members.size
        ? t(`${KEY}.selectMembership`)
        : !chosen.length
        ? t(`${KEY}.selectInstallment`)
        : total <= 0
        ? t(`${KEY}.nothingToCharge`)
        : null
    const confirmLabel = blocked ? (
        blocked
    ) : chargeNow ? (
        <Trans i18nKey={`${KEY}.confirmNow`} components={{ amount }} />
    ) : (
        <Trans
            i18nKey={`${KEY}.confirmOn`}
            components={{ amount }}
            values={{ date: formatISOString(date, DateFormats.SHORT_DATE) }}
        />
    )

    return (
        <Modal className="retry-payment-modal" title={t(`${KEY}.title`)} onDismiss={onClose}>
            <ModalBody>
                <div className="retry-intro">
                    <p>{t(`${KEY}.body`)}</p>
                    {membership.orderUuid && (
                        <a
                            className="order-link"
                            href={`/account/${accountSlug}/orders/${membership.orderUuid}/details`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t(`${KEY}.order`, { number: membership.orderNumber })}
                        </a>
                    )}
                </div>

                <section>
                    <div className="step">
                        <h5>{t(`${KEY}.memberships`)}</h5>
                        <button
                            type="button"
                            className="link-button"
                            onClick={() => pickMembers(new Set(allMembers ? [] : retry.memberships.map((m) => m.uuid)))}
                        >
                            {allMembers ? t(`${KEY}.clearAll`) : t(`${KEY}.selectAll`)}
                        </button>
                    </div>
                    {retry.memberships.map((m) => (
                        <label key={m.uuid} className={classNames('option', { on: members.has(m.uuid) })}>
                            <input
                                type="checkbox"
                                checked={members.has(m.uuid)}
                                onChange={() => toggleMember(m.uuid)}
                            />
                            <span>
                                <span className="name">
                                    {[m.member?.firstName, m.member?.lastName].filter(Boolean).join(' ') || m.email}{' '}
                                    <span className="muted">{m.membershipId}</span>
                                    {m.uuid === membership.uuid && (
                                        <span className="muted"> · {t(`${KEY}.thisMembership`)}</span>
                                    )}
                                </span>
                                <span className="sub">{m.article?.name}</span>
                            </span>
                            <Badge variant="error">
                                {t('memberships.membershipDetailsPanel.payments.status.overdue')}
                            </Badge>
                            <span className="amount">
                                <Currency
                                    amount={sum(retry.payments.filter((p) => p.membershipUuid === m.uuid))}
                                    currency={currency}
                                />
                            </span>
                        </label>
                    ))}
                </section>

                <section>
                    <div className="step">
                        <h5>{t(`${KEY}.installments`)}</h5>
                        <button
                            type="button"
                            className="link-button"
                            onClick={() => setMonthCount(monthCount === months.length ? 0 : months.length)}
                        >
                            {monthCount === months.length ? t(`${KEY}.clearAll`) : t(`${KEY}.selectAll`)}
                        </button>
                    </div>
                    {months.map((month, index) => {
                        const inMonth = retry.payments.filter((p) => isPicked(p) && monthOf(p) === month)
                        const dueDate = inMonth.map((p) => p.dueDate).sort()[0]
                        const failed = inMonth.filter((p) => p.status === MembershipPaymentStatus.FAILED)
                        const lastTry = failed
                            .map((p) => p.paymentDate)
                            .sort()
                            .pop()
                        return (
                            <label key={month} className={classNames('option', { on: index < monthCount })}>
                                <input
                                    type="checkbox"
                                    checked={index < monthCount}
                                    onChange={() => toggleMonth(index)}
                                />
                                <span>
                                    <span className="name">
                                        {formatISOString(dueDate, 'MMMM')}{' '}
                                        <span className="muted">
                                            ·{' '}
                                            {t(`${KEY}.dueOn`, {
                                                date: formatISOString(dueDate, DateFormats.SHORT_DATE),
                                            })}
                                        </span>
                                    </span>
                                    <span className="sub">
                                        {lastTry
                                            ? t(`${KEY}.failedDetail`, {
                                                  date: formatISOString(lastTry, DateFormats.SHORT_DATE),
                                              })
                                            : t(`${KEY}.overdueDetail`)}
                                    </span>
                                </span>
                                <Badge variant={lastTry ? 'error' : 'warning'}>
                                    {lastTry
                                        ? t('memberships.membershipDetailsPanel.payments.status.failed')
                                        : t('memberships.membershipDetailsPanel.payments.status.overdue')}
                                </Badge>
                                <span className="amount">
                                    <Currency amount={sum(inMonth)} currency={currency} />
                                </span>
                            </label>
                        )
                    })}
                    {nextDate && (
                        <div className="option future">
                            <span />
                            <span>
                                <span className="name">
                                    {formatISOString(nextDate, 'MMMM')}{' '}
                                    <span className="muted">
                                        ·{' '}
                                        {t(`${KEY}.dueOn`, { date: formatISOString(nextDate, DateFormats.SHORT_DATE) })}
                                    </span>
                                </span>
                                <span className="sub">{t(`${KEY}.upcomingDetail`)}</span>
                            </span>
                            <Badge variant="neutral">
                                {t('memberships.membershipDetailsPanel.payments.status.upcoming')}
                            </Badge>
                            <span className="amount">
                                <Currency amount={sum(upcoming)} currency={currency} />
                            </span>
                        </div>
                    )}
                    <div className="hint">{t(`${KEY}.oldestFirst`)}</div>
                </section>

                <section>
                    <div className="step">
                        <h5>{t(`${KEY}.when`)}</h5>
                    </div>
                    <div className="when">
                        <label>
                            <Radio name="retry-when" checked={chargeNow} onChange={() => setChargeNow(true)} />
                            {t(`${KEY}.chargeNow`)}
                        </label>
                        <label>
                            <Radio name="retry-when" checked={!chargeNow} onChange={() => setChargeNow(false)} />
                            {t(`${KEY}.chargeOn`)}
                        </label>
                        <DatePicker
                            id="retry-payment-date"
                            date={parseDate(date)}
                            onChange={pickDate}
                            lowerBoundary={parseDate(today)}
                            upperBoundary={parseDate(retry.latestDate)}
                            disabled={chargeNow}
                        />
                    </div>
                </section>

                <div className="total">
                    <div>
                        <small>
                            {chargeNow
                                ? t(`${KEY}.oneChargeNow`)
                                : t(`${KEY}.oneChargeOn`, { date: formatISOString(date, DateFormats.SHORT_DATE) })}
                        </small>
                        <span>
                            {t(`${KEY}.membershipCount`, { count: members.size })} ×{' '}
                            {t(`${KEY}.installmentCount`, { count: Math.min(monthCount, months.length) })}
                        </span>
                    </div>
                    <strong>{amount}</strong>
                </div>
            </ModalBody>
            <ModalFooter>
                <CancelButton onClick={onClose}>{t(`${KEY}.goBack`)}</CancelButton>
                <DisabledItemHoverInfotip active={!!blocked} infotipText={blocked ?? ''} width="16rem" toTheLeft>
                    <ActionButton
                        disabled={!!blocked}
                        onClick={() =>
                            onRetry({
                                relatedMembershipUuids: [...members],
                                throughDate,
                                paymentDate: chargeNow ? null : date,
                            })
                        }
                    >
                        {confirmLabel}
                    </ActionButton>
                </DisabledItemHoverInfotip>
            </ModalFooter>
        </Modal>
    )
}

export default RetryPaymentModal
